import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from "@mui/material";
import { CheckCircle, Cancel, Refresh } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import Header from "./Header";
import ApproveRequestDialog from "./ApproveRequestDialog";

const statusTabs = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All" },
];

const RequestedSongs = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState("pending");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/temp-song-requests?status=${tab}&sort=request_count&order=desc`
      );
      const data = await res.json();
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to load requested songs", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [tab, enqueueSnackbar]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleOpenApprove = (row) => {
    setSelectedRequest(row);
    setApproveDialogOpen(true);
  };

  const handleReject = async (id) => {
    setActionId(id);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/temp-song-requests/${id}/reject`,
        { method: "POST" }
      );
      if (!res.ok) throw new Error("Reject failed");
      enqueueSnackbar("Request rejected", { variant: "info" });
      fetchRequests();
    } catch (err) {
      enqueueSnackbar("Reject failed", { variant: "error" });
    } finally {
      setActionId(null);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#121210" }}>
      <Header />
      <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700 }}>
            Requested Songs
          </Typography>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchRequests} sx={{ color: "#1DB954" }}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", mb: 2 }}>
          Approve opens a form to set genre, Spotify ID, YouTube/Karaoke URLs, then saves to the catalog.
        </Typography>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            mb: 2,
            "& .MuiTab-root": { color: "rgba(255,255,255,0.6)" },
            "& .Mui-selected": { color: "#1DB954" },
            "& .MuiTabs-indicator": { bgcolor: "#1DB954" },
          }}
        >
          {statusTabs.map((t) => (
            <Tab key={t.value} value={t.value} label={t.label} />
          ))}
        </Tabs>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: "#1DB954" }} />
          </Box>
        ) : requests.length === 0 ? (
          <Typography sx={{ color: "rgba(255,255,255,0.5)", textAlign: "center", py: 4 }}>
            No requests in this tab.
          </Typography>
        ) : (
          <TableContainer component={Paper} sx={{ bgcolor: "#1a1a1a" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "#aaa" }} />
                  <TableCell sx={{ color: "#aaa" }}>Title</TableCell>
                  <TableCell sx={{ color: "#aaa" }}>Artist</TableCell>
                  <TableCell sx={{ color: "#aaa" }}>Source</TableCell>
                  <TableCell sx={{ color: "#aaa" }} align="center">
                    Requests
                  </TableCell>
                  <TableCell sx={{ color: "#aaa" }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      {row.thumbnail && (
                        <Box
                          component="img"
                          src={row.thumbnail}
                          alt=""
                          sx={{ width: 40, height: 40, borderRadius: 1, objectFit: "cover" }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ color: "#fff" }}>{row.title}</TableCell>
                    <TableCell sx={{ color: "rgba(255,255,255,0.8)" }}>{row.artist}</TableCell>
                    <TableCell>
                      <Chip label={row.source} size="small" sx={{ textTransform: "capitalize" }} />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={row.request_count}
                        color="primary"
                        size="small"
                        sx={{ fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {row.status === "pending" ? (
                        <>
                          <Button
                            size="small"
                            startIcon={<CheckCircle />}
                            onClick={() => handleOpenApprove(row)}
                            disabled={actionId === row.id}
                            sx={{ color: "#1DB954", mr: 1 }}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            startIcon={<Cancel />}
                            color="error"
                            onClick={() => handleReject(row.id)}
                            disabled={actionId === row.id}
                          >
                            Reject
                          </Button>
                        </>
                      ) : (
                        <Chip
                          label={row.status}
                          size="small"
                          color={row.status === "approved" ? "success" : "default"}
                        />
                      )}
                      {row.songs_master_id && (
                        <Typography variant="caption" sx={{ display: "block", color: "#888" }}>
                          Song #{row.songs_master_id}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <ApproveRequestDialog
        open={approveDialogOpen}
        request={selectedRequest}
        onClose={() => {
          setApproveDialogOpen(false);
          setSelectedRequest(null);
        }}
        onApproved={fetchRequests}
      />
    </Box>
  );
};

export default RequestedSongs;
